import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2, Separator, Text } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { CoordinatedAccountRow } from './components/CoordinatedAccountRow';
import {
  CoordinatedAccountsCardDocument,
  CoordinatedAccountsCardQuery,
} from './graphql/coordinatedAccountsCard.generated';

import { trackClick } from '../../../features/track';
import { urlBuilder } from '../../../Shared/urlBuilder';

type ManagedAccount = CoordinatedAccountsCardQuery['account'] & { __typename: 'ManagedAccount' };
type ManagedSubAccounts = ManagedAccount['subAccounts'];

const CoordinatedAccountsCardView: React.FC = () => {
  const returnToPath = window.location.pathname;
  const accountId = useRequiredParam('accountId');
  const { data } = useGraphQLQuery(CoordinatedAccountsCardDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  const { account } = data;
  const subAccounts: ManagedSubAccounts = account.__typename === 'ManagedAccount' ? account.subAccounts : [];
  if (subAccounts.length <= 1) return null;

  return (
    <Card>
      <Card.Header
        css={{
          padding: 'space.300',
          display: 'flex',
          gap: 'space.50',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <H2 variant="title200">Tax-coordinated accounts</H2>
        <Text variant="text50" color="text.weak">
          Balance
        </Text>
      </Card.Header>
      <Card.Content>
        {subAccounts.map(subAccount => (
          <CoordinatedAccountRow key={subAccount.id} subAccount={subAccount} />
        ))}
      </Card.Content>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Footer css={{ padding: 'space.300' }}>
        <SambaAutoLink
          to={urlBuilder.addGoal.absolute({ return_to: returnToPath })}
          onClick={() => trackClick({ name: 'add_account_coordinated_accounts_card' })}
        >
          Add new account
        </SambaAutoLink>
      </Card.Footer>
    </Card>
  );
};

export const CoordinatedAccountsCard: React.FC = () => (
  <ErrorBoundary fallbackType="none">
    <CoordinatedAccountsCardView />
  </ErrorBoundary>
);
